import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import {
  changePassword,
  confirmPasswordReset,
  deletePasskey,
  fetchMe,
  fetchPasskeys,
  login,
  logout,
  passkeyLoginBegin,
  passkeyLoginFinish,
  passkeyRegisterBegin,
  passkeyRegisterFinish,
  requestPasswordReset,
} from './api'
import { clearPersistedCache } from '@/lib/persist'
import type { Flow } from '@/lib/flows'
import type { SessionUser } from '@/types/auth'

export const AUTH_KEYS = {
  me: ['auth', 'me'] as const,
  passkeys: ['auth', 'passkeys'] as const,
}

// A fresh sign-in must not inherit the previous user's restored data on a
// shared machine — when the user changes, clear both caches before seeding
// the new session. Same user re-authenticating keeps their warm cache.
export function seedSession(qc: QueryClient, user: SessionUser): void {
  const previous = qc.getQueryData<SessionUser | null>(AUTH_KEYS.me)
  if (previous?.id === user.id) {
    qc.setQueryData(AUTH_KEYS.me, user)
    return
  }
  void clearPersistedCache().then(() => qc.setQueryData(AUTH_KEYS.me, user))
}

export function useMe() {
  return useQuery({
    queryKey: AUTH_KEYS.me,
    queryFn: fetchMe,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
}

export function useLogin() {
  const qc = useQueryClient()
  return useMutation({
    // Session-lifecycle requests must fail fast while offline, never queue:
    // 'always' attempts the request regardless of connectivity and errors on
    // failure (the default 'online' mode would pause-and-replay instead —
    // a queued login/logout firing later is wrong).
    networkMode: 'always',
    mutationFn: ({ username, password }: { username: string; password: string }) =>
      login(username, password),
    onSuccess: (user) => seedSession(qc, user),
  })
}

export function useLogout() {
  return useMutation({
    networkMode: 'always',
    mutationFn: () => logout(),
    onSuccess: () => {
      // The server session is revoked — drop everything, including the
      // persisted cache. On a failed logout (e.g. offline) keep the local
      // session: the cookie is still valid server-side.
      void clearPersistedCache()
    },
  })
}

export function usePasskeyLogin() {
  const qc = useQueryClient()
  return useMutation({
    networkMode: 'always',
    mutationFn: async (username?: string) => {
      const { get } = await import('@github/webauthn-json')
      const begin = await passkeyLoginBegin(username)
      const assertion = await get({ publicKey: begin.options })
      return passkeyLoginFinish(begin.ceremony_id, assertion)
    },
    onSuccess: (user) => seedSession(qc, user),
  })
}

export function usePasskeys() {
  return useQuery({
    queryKey: AUTH_KEYS.passkeys,
    queryFn: fetchPasskeys,
  })
}

export function useRegisterPasskey() {
  const qc = useQueryClient()
  return useMutation({
    networkMode: 'always',
    mutationFn: async (name: string) => {
      const { create } = await import('@github/webauthn-json')
      const begin = await passkeyRegisterBegin()
      const attestation = await create({ publicKey: begin.options })
      return passkeyRegisterFinish(begin.ceremony_id, name, attestation)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: AUTH_KEYS.passkeys }),
  })
}

export function useDeletePasskey() {
  const qc = useQueryClient()
  return useMutation({
    networkMode: 'always',
    mutationFn: (id: string) => deletePasskey(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: AUTH_KEYS.passkeys }),
  })
}

// Changing the password re-issues the session cookie (revoking all other
// devices); refresh me so has_password flips after an initial set.
export function useChangePassword() {
  const qc = useQueryClient()
  return useMutation({
    networkMode: 'always',
    mutationFn: ({ current, next }: { current: string; next: string }) =>
      changePassword(current, next),
    onSuccess: (user) => qc.setQueryData(AUTH_KEYS.me, user),
  })
}

export function useRequestPasswordReset() {
  return useMutation({
    networkMode: 'always',
    mutationFn: (login: string) => requestPasswordReset(login),
  })
}

// Confirming a reset signs the user in — mirror the login hooks.
export function useConfirmPasswordReset() {
  const qc = useQueryClient()
  return useMutation({
    networkMode: 'always',
    mutationFn: ({ token, next }: { token: string; next: string }) =>
      confirmPasswordReset(token, next),
    onSuccess: (user) => seedSession(qc, user),
  })
}

// useIsAdmin reports whether the signed-in user is a full admin (account
// admin role or fp-admin): user/role management and the dashboard.
export function useIsAdmin(): boolean {
  const { data: me } = useMe()
  return !!me && (me.role === 'admin' || me.role === 'fp_admin')
}

// useCanAccessFlow reports whether the signed-in user's role grants the flow
// (read and write). Admins and fp-admins have every flow.
export function useCanAccessFlow(flow: Flow): boolean {
  const { data: me } = useMe()
  return !!me && me.flows.includes(flow)
}

export type { SessionUser }
